/**
 * TanStack Query Hook for Tax Generated All Time KPI
 *
 * This hook provides access to the tax generated all time KPI data from the database.
 * It uses TanStack Query for efficient data fetching with automatic caching,
 * background refetching, and error handling.
 *
 * Database Source: web.v_kpi_tax_generated_all_time_v1 view
 *
 * @module useKpiTaxGeneratedAllTimeV1
 * @category Database Hooks
 *
 * @example
 * ```typescript
 * // Basic usage in a component
 * import { useKpiTaxGeneratedAllTimeV1 } from '@/hooks/database/useKpiTaxGeneratedAllTimeV1';
 *
 * function TaxGeneratedDisplay() {
 *   const { data, isLoading, error } = useKpiTaxGeneratedAllTimeV1();
 *
 *   if (isLoading) return <div>Loading...</div>;
 *   if (error) return <div>Error: {error.message}</div>;
 *
 *   return (
 *     <div>
 *       <h2>Tax Generated All Time</h2>
 *       <p>Amount: {data.tax_amount_msek} MSEK</p>
 *       <p>Last Updated: {new Date(data.calculation_date).toLocaleDateString()}</p>
 *     </div>
 *   );
 * }
 * ```
 *
 * @example
 * ```typescript
 * // Advanced usage with refetch control
 * function KpiDashboard() {
 *   const { data, isLoading, error, refetch, isFetching } = useKpiTaxGeneratedAllTimeV1();
 *
 *   const handleRefresh = () => {
 *     refetch();
 *   };
 *
 *   return (
 *     <div>
 *       <button onClick={handleRefresh} disabled={isFetching}>
 *         {isFetching ? 'Refreshing...' : 'Refresh Data'}
 *       </button>
 *       {data && <KpiCard kpi={data} />}
 *     </div>
 *   );
 * }
 * ```
 */

import { useQuery, UseQueryResult, QueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { KpiTaxGeneratedAllTime } from '@/types/kpi.types';

/**
 * Fetches tax generated all time KPI data from the database
 *
 * This function queries the web.v_kpi_tax_generated_all_time_v1 view and returns
 * a single row with the total tax generated amount.
 *
 * @returns {Promise<KpiTaxGeneratedAllTime>} KPI data object with tax amount and metadata
 * @throws {Error} If the database query fails or returns invalid data
 *
 * @internal
 */
const fetchKpiTaxGeneratedAllTime = async (): Promise<KpiTaxGeneratedAllTime> => {
  const { data, error } = await supabase
    .schema('web')
    .from('v_kpi_tax_generated_all_time_v1')
    .select('*')
    .single();

  if (error) {
    throw new Error(`Failed to fetch tax generated all time KPI: ${error.message}`);
  }

  if (!data) {
    throw new Error('No data returned from tax generated all time KPI query');
  }

  return data;
};

/**
 * React Hook for Tax Generated All Time KPI Data
 *
 * Fetches and caches the tax generated all time KPI data using TanStack Query.
 * The data is cached for 1 hour and will not automatically refetch on
 * window focus or component mount.
 *
 * Query Configuration:
 * - Query Key: ['kpi', 'tax-generated-all-time', 'v1']
 * - Stale Time: 1 hour (3600000ms)
 * - Refetch on Window Focus: false
 * - Refetch on Mount: false
 * - Retry Attempts: 2
 *
 * @returns {UseQueryResult<KpiTaxGeneratedAllTime>} TanStack Query result object
 * @property {KpiTaxGeneratedAllTime | undefined} data - The KPI data (undefined during loading)
 * @property {boolean} isLoading - True if query is in initial loading state
 * @property {boolean} isFetching - True if query is fetching (including background fetches)
 * @property {boolean} isError - True if query encountered an error
 * @property {Error | null} error - The error object if query failed
 * @property {Function} refetch - Function to manually refetch the data
 *
 * @example
 * ```typescript
 * // Simple usage
 * const { data, isLoading } = useKpiTaxGeneratedAllTimeV1();
 * console.log(data?.tax_amount_msek); // e.g., 1250.5
 * ```
 *
 * @example
 * ```typescript
 * // Error handling
 * const { data, isLoading, isError, error } = useKpiTaxGeneratedAllTimeV1();
 *
 * if (isLoading) {
 *   return <Spinner />;
 * }
 *
 * if (isError) {
 *   return <ErrorAlert message={error?.message} />;
 * }
 *
 * return <KpiDisplay value={data.tax_amount_msek} />;
 * ```
 *
 * @example
 * ```typescript
 * // Manual refetch
 * const { data, refetch } = useKpiTaxGeneratedAllTimeV1();
 *
 * const handleUpdate = async () => {
 *   await refetch();
 *   toast.success('KPI data refreshed');
 * };
 * ```
 */
export const useKpiTaxGeneratedAllTimeV1 = (): UseQueryResult<KpiTaxGeneratedAllTime> => {
  return useQuery<KpiTaxGeneratedAllTime>({
    queryKey: ['kpi', 'tax-generated-all-time', 'v1'],
    queryFn: fetchKpiTaxGeneratedAllTime,
    staleTime: 3600000, // 1 hour in milliseconds
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
  });
};

/**
 * Query Key Factory for Tax Generated All Time KPI
 *
 * Provides consistent query key generation for TanStack Query.
 * Useful for manual cache manipulation or query invalidation.
 *
 * @returns {string[]} The query key array
 *
 * @example
 * ```typescript
 * import { useQueryClient } from '@tanstack/react-query';
 * import { getKpiTaxGeneratedAllTimeQueryKey } from '@/hooks/database/useKpiTaxGeneratedAllTimeV1';
 *
 * function RefreshButton() {
 *   const queryClient = useQueryClient();
 *
 *   const invalidateCache = () => {
 *     queryClient.invalidateQueries({
 *       queryKey: getKpiTaxGeneratedAllTimeQueryKey()
 *     });
 *   };
 *
 *   return <button onClick={invalidateCache}>Clear Cache</button>;
 * }
 * ```
 */
export const getKpiTaxGeneratedAllTimeQueryKey = (): string[] => {
  return ['kpi', 'tax-generated-all-time', 'v1'];
};

/**
 * Prefetch Function for Tax Generated All Time KPI
 *
 * Allows prefetching the KPI data before component mount.
 * Useful for optimistic data loading and improving perceived performance.
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient - TanStack Query client instance
 * @returns {Promise<void>}
 *
 * @example
 * ```typescript
 * import { useQueryClient } from '@tanstack/react-query';
 * import { prefetchKpiTaxGeneratedAllTime } from '@/hooks/database/useKpiTaxGeneratedAllTimeV1';
 *
 * function App() {
 *   const queryClient = useQueryClient();
 *
 *   useEffect(() => {
 *     // Prefetch KPI data on app load
 *     prefetchKpiTaxGeneratedAllTime(queryClient);
 *   }, [queryClient]);
 *
 *   return <Dashboard />;
 * }
 * ```
 */
export const prefetchKpiTaxGeneratedAllTime = async (
  queryClient: QueryClient
): Promise<void> => {
  await queryClient.prefetchQuery({
    queryKey: getKpiTaxGeneratedAllTimeQueryKey(),
    queryFn: fetchKpiTaxGeneratedAllTime,
    staleTime: 3600000,
  });
};
